import {useTranslation} from "react-i18next";
import type {InsightDistributionValueV4} from "../../models/crm-v4";
import type {InsightsLoading} from "../../kit/insightsLoading";
import CrmBucketsCard, {bucketRows} from "./CrmBucketsCard";
import CrmShareCard from "./CrmShareCard";

interface DistributionCardProps {
    items: InsightDistributionValueV4[];
    loading?: InsightsLoading;
    comparing: boolean;
}

const AGE_LABELS: Record<string, string> = {
    UNDER_18: "Under 18",
    "18_24": "18–24",
    "25_34": "25–34",
    "35_44": "35–44",
    "45_54": "45–54",
    "55_64": "55–64",
    "65_PLUS": "65+",
    UNKNOWN: "Unknown",
};

const AGE_CATEGORY_LABELS: Record<string, string> = {
    ADULT: "Adult",
    CHILD: "Child",
    TEENAGER: "Teenager",
    INFANT: "Infant",
};

const PARTY_SIZE_LABELS: Record<string, string> = {
    "1": "1 guest",
    "2": "2 guests",
    "3": "3 guests",
    "4": "4 guests",
    "5_6": "5–6 guests",
    "7_PLUS": "7+ guests",
};

export const GENDER_LABELS: Record<string, string> = {
    MALE: "Male",
    FEMALE: "Female",
    NON_BINARY: "Non-binary",
    GENDERQUEER: "Genderqueer",
    GENDERFLUID: "Genderfluid",
    AGENDER: "Agender",
    TWO_SPIRIT: "Two-spirit",
    BIGENDER: "Bigender",
    DEMIBOY: "Demiboy",
    DEMIGIRL: "Demigirl",
    ANDROGYNOUS: "Androgynous",
    NEUTROIS: "Neutrois",
    OTHER: "Other",
    UNKNOWN: "Unknown",
};

export function AgeCategoriesCard({items, loading, comparing}: DistributionCardProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const countFormatter = new Intl.NumberFormat(locale, {maximumFractionDigits: 0});

    return (
        <CrmShareCard
            title={t("Age categories")}
            hint={t("Headcount across bookings by age category.")}
            rows={items.map(item => ({
                key: item.key,
                label: t(AGE_CATEGORY_LABELS[item.key] ?? item.key),
                value: item.value,
                compareValue: item.compareValue,
            }))}
            loading={loading}
            comparing={comparing}
            format={value => `${countFormatter.format(value)} ${t("guests")}`}
            valueLabel={t("Guests")}
            empty={t("No data in the selected period")}
        />
    );
}

export function PartySizeCard({items, loading, comparing}: DistributionCardProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const countFormatter = new Intl.NumberFormat(locale, {maximumFractionDigits: 0});
    const compactFormatter = new Intl.NumberFormat(locale, {notation: "compact", maximumFractionDigits: 1});

    return (
        <CrmBucketsCard
            title={t("Party size")}
            hint={t("Bookings grouped by number of guests.")}
            rows={bucketRows(items, key => t(PARTY_SIZE_LABELS[key] ?? key))}
            loading={loading}
            comparing={comparing}
            format={value => `${countFormatter.format(value)} ${t("bookings")}`}
            formatTick={value => compactFormatter.format(value)}
            valueLabel={t("Bookings")}
            empty={t("No data in the selected period")}
        />
    );
}

export function AgeDistributionCard({items, loading, comparing}: DistributionCardProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const compactFormatter = new Intl.NumberFormat(locale, {notation: "compact", maximumFractionDigits: 1});

    return (
        <CrmBucketsCard
            title={t("Age distribution")}
            hint={t("Guests who arrived in the selected period, grouped by the birth dates on file")}
            rows={bucketRows(items, key => t(AGE_LABELS[key] ?? key))}
            loading={loading}
            comparing={comparing}
            format={value => t("{{count}} guests", {count: value})}
            formatTick={value => compactFormatter.format(value)}
            valueLabel={t("Guests")}
            empty={t("No data in the selected period")}
        />
    );
}

export function GenderSplitCard({items, loading, comparing}: DistributionCardProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const countFormatter = new Intl.NumberFormat(locale, {maximumFractionDigits: 0});
    const genderKeys = Object.keys(GENDER_LABELS);

    return (
        <CrmShareCard
            title={t("Gender split")}
            hint={t("Gender values on the arriving guests' profiles; hidden and missing values are Unknown")}
            rows={items.map(item => ({
                key: item.key,
                label: t(GENDER_LABELS[item.key] ?? item.key),
                value: item.value,
                compareValue: item.compareValue,
                // A gender keeps its colour whether or not the ones before it were reported at all.
                rank: Math.max(0, genderKeys.indexOf(item.key)),
            }))}
            loading={loading}
            comparing={comparing}
            format={value => `${countFormatter.format(value)} ${t("guests")}`}
            valueLabel={t("Guests")}
            empty={t("No data in the selected period")}
        />
    );
}
